import { NextResponse } from 'next/server'
import { and, eq } from 'drizzle-orm'
import { db } from '@/db'
import { auditLog, tickets } from '@/db/schema'
import { inngest } from '@/lib/inngest'
import { requireExpertApiUser } from '@/lib/role-guard'
import { transition } from '@/lib/ticket-state-machine'

export async function POST(
  _request: Request,
  { params }: { params: Promise<{ ticketId: string }> },
) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const { ticketId } = await params
  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, ticketId), eq(tickets.tenantId, guard.user.tenantId)))
    .limit(1)

  if (!ticket) return NextResponse.json({ error: 'Ticket not found' }, { status: 404 })
  if (!['NEEDS_INFO', 'AWAITING_EXPERT', 'EXPERT_REVIEWING', 'RESPONSE_READY'].includes(ticket.status)) {
    return NextResponse.json(
      { error: `Cannot draft a response while ticket is ${ticket.status}` },
      { status: 409 },
    )
  }

  const nextStatus = transition(ticket.status, 'RESPONSE_DRAFTING')
  const [updated] = await db
    .update(tickets)
    .set({
      status: nextStatus,
      aiProcessingState: 'queued',
      aiFailureReason: null,
      assignedExpertId: guard.user.id,
      version: ticket.version + 1,
      updatedAt: new Date(),
    })
    .where(and(eq(tickets.id, ticket.id), eq(tickets.version, ticket.version)))
    .returning()

  if (!updated) return NextResponse.json({ error: 'Ticket was updated, retry draft request' }, { status: 409 })

  await db.insert(auditLog).values({
    tenantId: guard.user.tenantId,
    actorId: guard.user.id,
    actorType: 'user',
    action: 'ticket.response_draft_requested',
    entityType: 'ticket',
    entityId: ticket.id,
    payload: { from: ticket.status, to: nextStatus },
  })

  await inngest.send({
    name: 'ticket/response_draft.requested',
    data: {
      ticketId: ticket.id,
      tenantId: guard.user.tenantId,
      expertId: guard.user.id,
      ticketVersion: updated.version,
    },
  })

  return NextResponse.json({ ticketId: ticket.id, status: updated.status })
}
